import moment from "moment";
import { toast } from "react-toastify";
import { Row, StatusKey } from "./types";
import { AgendaController } from "./useAgendaState";

export function useReportTransitions(ctl: AgendaController) {
  const handleTransition = (r: Row, kind: string) => {
    const [op, arg] = kind.split(":");

    if (op === "submitForReview") {
      ctl.dispatch({
        type: "SHOW_CONFIRM",
        payload: {
          title: "Submit for review",
          body: `Hand ${r.type} for project ${r.proj} to the reviewer? It leaves your grid until they respond.`,
          label: "Submit",
          onConfirm: () => {
            ctl.dispatch({
              type: "TRANSITION",
              patch: { id: r.id, st: "review", owner: "jennifer.bravo" },
            });
            toast.success("Submitted for review");
          },
        },
      });
    } else if (op === "sendBack") {
      const stage = arg as "review" | "verify" | "approval";
      const n =
        stage === "review" ? ctl.ckPending.length : ctl.cmHeld(stage).length;
      ctl.dispatch({
        type: "SHOW_CONFIRM",
        payload: {
          title: "Send back to writer",
          body: `Send ${n} item${n > 1 ? "s" : ""} to ${r.writer}? They become visible to the writer and the report leaves your queue until it returns.`,
          label: "Send back",
          onConfirm: () => {
            if (stage !== "review")
              ctl.dispatch({ type: "RELEASE_HELD", stage });
            const stMap: Record<string, StatusKey> = {
              review: "corr_rev",
              verify: "corr_ver",
              approval: "corr_app",
            };
            ctl.dispatch({
              type: "TRANSITION",
              patch: {
                id: r.id,
                st: stMap[stage],
                owner: r.writer,
                backBy: ctl.me(),
                backAt: "just now",
                backFrom:
                  stage === "review"
                    ? "Review"
                    : stage === "verify"
                    ? "Verification"
                    : "Approval",
              },
            });
            toast.success("Sent back to " + r.writer);
          },
        },
      });
    } else if (op === "returnTo") {
      const stage = arg as "review" | "verify" | "approval";
      const target =
        stage === "review"
          ? "reviewer"
          : stage === "verify"
          ? "verifier"
          : "approver";
      ctl.dispatch({
        type: "SHOW_CONFIRM",
        payload: {
          title: `Send back to ${target}`,
          body: `Return this report to the ${target} with all items marked as fixed? This becomes edit E${(r.edit || 1) + 1}.`,
          label: "Send back",
          onConfirm: () => {
            const stMap: Record<string, StatusKey> = {
              review: "review",
              verify: "verify",
              approval: "approval",
            };
            const ownerMap: Record<string, string> = {
              review: "jennifer.bravo",
              verify: "edgar.ibarra",
              approval: "jeff.cleland",
            };
            ctl.dispatch({
              type: "TRANSITION",
              patch: {
                id: r.id,
                edit: (r.edit || 1) + 1,
                st: stMap[stage],
                owner: ownerMap[stage],
              },
            });
            toast.success(`Returned to ${target} as E${(r.edit || 1) + 1}`);
          },
        },
      });
    } else if (op === "passOn") {
      const to = arg as "verify" | "approval";
      const label = to === "verify" ? "verifier" : "approver";
      ctl.dispatch({
        type: "SHOW_CONFIRM",
        payload: {
          title: `Pass to ${label}`,
          body: `Hand this report to the ${label}? It leaves your grid — your part is done.`,
          label: "Pass on",
          onConfirm: () => {
            ctl.dispatch({
              type: "TRANSITION",
              patch: {
                id: r.id,
                st: to,
                owner: to === "verify" ? "edgar.ibarra" : "jeff.cleland",
              },
            });
            toast.success("Passed to " + label);
          },
        },
      });
    } else if (op === "approveAndPdf") {
      ctl.dispatch({
        type: "SHOW_CONFIRM",
        payload: {
          title: "Approve and generate PDF",
          body: "This approves the report and generates the PDF in one step. The report is locked and clears from every grid, including the verifier's copy.",
          label: "Approve",
          onConfirm: () => {
            const pdf = `${r.proj}_${r.type.replace(
              /[^A-Za-z0-9]+/g,
              "_"
            )}_E${r.edit || 1}.pdf`;
            ctl.dispatch({
              type: "TRANSITION",
              patch: {
                id: r.id,
                st: "approved",
                owner: "—",
                doneAt: moment().format("DD-MM-YYYY HH:mm"),
                approvedBy: ctl.me(),
                pdf,
              },
            });
            toast.success("Approved — PDF generated");
          },
        },
      });
    }
  };

  return { handleTransition };
}